import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Star, CheckCircle2, Calendar, Clock, BookmarkPlus, Loader2, Bookmark } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=600";

export default function PublicMovieDetailDialog({ movie, open, onOpenChange, canCopy, copying, onCopy }) {
  if (!movie) return null;
  const seasons = movie.seasons || [];
  const isTV = movie.media_type === "tv" && seasons.length > 0;
  const watchedSeasons = seasons.filter(s => s.status === "watched").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F12] border-white/10 text-slate-100 max-w-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto" data-testid={`public-detail-${movie.id}`}>
        <div className="relative">
          {movie.poster && (
            <div className="absolute inset-0 opacity-20 blur-2xl"
                 style={{ backgroundImage: `url(${movie.poster})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-[#0F0F12]/80 to-[#0F0F12]" />
          <div className="relative grid md:grid-cols-[200px_1fr] gap-6 p-6">
            <div className="aspect-[2/3] rounded-lg overflow-hidden bg-[#1A1A1E] shadow-2xl mx-auto md:mx-0 max-w-[200px] w-full">
              <img src={movie.poster || FALLBACK} alt={movie.title}
                   className="w-full h-full object-cover"
                   onError={e => { e.target.src = FALLBACK; }} />
            </div>
            <div className="space-y-3 min-w-0">
              <div>
                <h2 className="font-display text-3xl font-bold tracking-tight">{movie.title}</h2>
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400 mt-2">
                  {movie.year && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{movie.year}</span>}
                  {movie.runtime && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{movie.runtime}</span>}
                  {movie.imdb_rating && movie.imdb_rating !== "N/A" && parseFloat(movie.imdb_rating) > 0 && (
                    <span className="flex items-center gap-1"><Star className="w-3 h-3 text-[#F59E0B] fill-[#F59E0B]" />{Number(parseFloat(movie.imdb_rating).toFixed(1))}</span>
                  )}
                  {isTV && <span className="px-1.5 py-0.5 rounded bg-white/10 text-[10px]">TV · {watchedSeasons}/{seasons.length} S</span>}
                  {!isTV && movie.status === "watched" && (
                    <span className="flex items-center gap-1 text-[#10B981]"><CheckCircle2 className="w-3 h-3" /> Watched</span>
                  )}
                  {!isTV && movie.status !== "watched" && (
                    <span className="flex items-center gap-1 text-slate-400"><Bookmark className="w-3 h-3" /> Backlog</span>
                  )}
                  {isTV && watchedSeasons === seasons.length && watchedSeasons > 0 && (
                    <span className="flex items-center gap-1 text-[#10B981]"><CheckCircle2 className="w-3 h-3" /> Watched</span>
                  )}
                </div>
                {movie.genre && <div className="text-xs text-slate-500 mt-1">{movie.genre}</div>}
                {movie.director && <div className="text-xs text-slate-500">Dir. {movie.director}</div>}
              </div>

              {movie.plot && <p className="text-sm text-slate-300 leading-relaxed">{movie.plot}</p>}

              {(movie.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {movie.tags.map(t => (
                    <span key={t} className="px-2 py-0.5 rounded-full text-[10px] bg-white/5 border border-white/10 text-slate-300">#{t}</span>
                  ))}
                </div>
              )}

              {canCopy && (
                <div className="pt-2">
                  <Button onClick={() => onCopy(movie.id)} disabled={copying} data-testid={`public-detail-copy-${movie.id}`}
                          className="bg-[#E11D48] hover:bg-rose-700 rounded-full">
                    {copying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <BookmarkPlus className="w-4 h-4 mr-1.5" />}
                    Copy to my library
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {isTV && (
          <div className="px-6 pb-6 pt-5 border-t border-white/5 bg-[#0F0F12] relative">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">
              Seasons <span className="normal-case text-slate-500 ml-1">· {watchedSeasons}/{seasons.length} watched by @owner</span>
            </div>
            <div className="space-y-2">
              {seasons.map(s => {
                const watched = s.status === "watched";
                return (
                  <div key={s.season_number}
                       data-testid={`public-season-${s.season_number}`}
                       className={`flex items-center gap-3 p-3 rounded-lg border ${
                         watched ? "bg-[#10B981]/10 border-[#10B981]/30" : "bg-[#1A1A1E] border-white/5"
                       }`}>
                    {s.poster ? (
                      <img src={s.poster} alt="" className="w-10 h-14 object-cover rounded" />
                    ) : (
                      <div className="w-10 h-14 bg-white/5 rounded flex items-center justify-center text-xs text-slate-500">
                        S{s.season_number}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium flex items-center gap-2">
                        {s.name || `Season ${s.season_number}`}
                        {watched && <CheckCircle2 className="w-4 h-4 text-[#10B981]" />}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-3">
                        {s.episode_count ? <span>{s.episode_count} eps</span> : null}
                        {s.air_date ? <span>· {s.air_date.slice(0, 4)}</span> : null}
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${watched ? "bg-[#10B981]/20 text-[#10B981]" : "bg-white/5 text-slate-400"}`}>
                      {watched ? "Watched" : "Backlog"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
